import {useRef, useState, useEffect, useCallback} from 'react';
import {useLocation} from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';

import {usePathname} from 'src/routes/hooks';
import {useActiveLink} from 'src/routes/hooks/use-active-link';

import NavItem from './nav-item';
import {NavListProps, NavSubListProps} from '../types';
import {
  BASE_MENU_ITEM,
  BASE_ROUTE,
  BASE_ROUTE_MODULE
} from 'src/routes/BaseRoute';
import {useTheme} from '@mui/material';
import { createAuditLog } from '@shared/services/service-handlers/PrivateService';
import AuthUtils from 'src/utils/AuthUtils';
import { useAppContext } from '@context/AppContext';

// ----------------------------------------------------------------------

export default function NavList({
  data,
  depth,
  slotProps,
  url,
  subListPath
}: NavListProps) {
  const navRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();

  const pathname = usePathname();
  const location = useLocation();

  const {
    appState: {applicationName}
  } = useAppContext();

  const isActive =
    location.pathname?.replace(/\/$/, '') ===
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${subListPath}` &&
    location.search === `?${BASE_MENU_ITEM}=${url}`;

  const activeLink = useActiveLink(
    `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/` + data.url,
    !!data?.listMenuItems && data?.listMenuItems?.length > 0
  );

  if (activeLink) {
    let eventDescription = `User accessed module : ${data.url}`;

    if (location.search?.length) {
      eventDescription += `, User accessed sub list: ${location.search.replace('?menu_item=', '')}`;
    }
    const dataObj = {
      isBH_selected: true,
      isBH_reason: '',
      application_name: applicationName,
      event_description: eventDescription,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp()
    };
    createAuditLog(data.url, dataObj);
  }

  const active = subListPath && url ? isActive : activeLink;

  const [openMenu, setOpenMenu] = useState(false);

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(false);
  }, []);

  useEffect(() => {
    if (openMenu) {
      handleCloseMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpenMenu = useCallback(() => {
    if (data.listMenuItems && data?.listMenuItems?.length > 0) {
      setOpenMenu(true);
    }
  }, [data.listMenuItems]);

  return (
    <>
      <NavItem
        ref={navRef}
        open={openMenu}
        onMouseEnter={handleOpenMenu}
        onMouseLeave={handleCloseMenu}
        //
        name={data.name}
        url={
          url
            ? `${
                subListPath?.length
                  ? `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${subListPath}/`
                  : ''
              }?${BASE_MENU_ITEM}=${url}`
            : `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/` + data.url
        }
        icon={data.icon}
        info={data.info}
        roles={data.roles}
        caption={data.caption}
        disabled={data.disabled}
        //
        depth={depth}
        hasChild={data?.listMenuItems?.length > 0 && !!data.listMenuItems}
        externalLink={data.url.includes('http')}
        currentRole={slotProps?.currentRole}
        //
        active={active}
        className={active ? 'active' : ''}
        sx={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
        path={''}
      />

      {!!data?.listMenuItems && data?.listMenuItems?.length > 0 && (
        <Popover
          disableScrollLock
          open={openMenu}
          anchorEl={navRef.current}
          anchorOrigin={{vertical: 'center', horizontal: 'right'}}
          transformOrigin={{vertical: 'center', horizontal: 'left'}}
          slotProps={{
            paper: {
              onMouseEnter: handleOpenMenu,
              onMouseLeave: handleCloseMenu,
              style: {
                backgroundColor: theme.palette.primary.darker
              },
              sx: {
                mt: 0.5,
                minWidth: 160,
                ...(openMenu && {
                  pointerEvents: 'auto'
                })
              }
            }
          }}
          sx={{
            pointerEvents: 'none'
          }}>
          <NavSubList
            data={data.listMenuItems}
            depth={depth}
            slotProps={slotProps}
            url={data.url}
          />
        </Popover>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function NavSubList({data, depth, slotProps, url}: NavSubListProps) {
  return (
    <Stack spacing={0.5}>
      {data.map(list => (
        <NavList
          key={list.name}
          data={list}
          depth={depth + 1}
          slotProps={slotProps}
          url={list.url}
          subListPath={url}
        />
      ))}
    </Stack>
  );
}
